import React, { useEffect, useState } from 'react';
import ReactGA from "react-ga4";
import { Empty, Row, Col, Avatar, Comment, Typography, Space } from 'antd';
import { CaretRightOutlined, TwitterOutlined  } from '@ant-design/icons';
import { TwitterShareButton } from "react-share";

import PlayArea from './playArea';
import { DotProps, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';


const { Text } = Typography;

type Props = {
  channel: any,
  title: string,
  videoId: string,
  thumbnail: string,
}

interface LevelGraphItem {
  level: string,
  time: number,
}
interface RechartsDotPayload extends React.MouseEvent<SVGCircleElement, MouseEvent> {
  payload: LevelGraphItem
};


function GraphArea(props: Props) {
  const [levelGraph, setLevelGraph] = useState([]);
  const [startAt, setStartAt] = useState(0);
  const [maxLevel, setMaxLevel] = useState(10);
  const [maxTime, setMaxTime] = useState(10);
  const [autoplay, setAutoplay] = useState(false);

  useEffect(() => {
    if(process.env.REACT_APP_REACT_GA){
      ReactGA.initialize(process.env.REACT_APP_REACT_GA as string);
      ReactGA.send({
        hitType: "pageview",
        page: '/hololive/GraphArea/' + props.videoId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(() => {
    try {
      fetch(process.env.REACT_APP_DATA_PATH + 'results/' + props.videoId + '/excite_level.json', {method: 'GET'})
      .then(res => res.json())
      .then(data => {
        setLevelGraph(data.level_graph);
        setMaxLevel(data.top3[0].level);
        setMaxTime(data.top3[0].time);
      });
    } catch(e) {
      console.log(e)
      console.log('Analyze data not found. :' + props.videoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const secToText = (sec: number) => {
    const hour = Math.floor(sec / 3600);
    const min = Math.floor((sec - hour*60) / 60);
    const rem =  Math.floor(sec % 60);

    
    return hour === 0 ? `${min}:${rem}` : `${hour}:${min}:${rem}`
  }

  return (
    <div className='GraphArea'>
      <Row align="middle">
        <Col xs={24} md={12} xl={12}>
          <PlayArea
            videoId={props.videoId}
            title={props.title}
            thumbnail={props.thumbnail}
            startAt={startAt}
            autoplay={autoplay}
          />
        </Col>
        <Col xs={24} md={12} xl={12}>
          {levelGraph.length === 0
            ? <Empty />
            : <div>
                <ResponsiveContainer width='100%' aspect={16.0/9.0}>
                  <LineChart
                    data={levelGraph}
                    margin={{
                      top: 5,
                      right: 5,
                      left: 5,
                      bottom: 5
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" unit="秒" dataKey="time" domain={['開始', '終了']}/>
                    <YAxis type="number" unit="%" hide={true}/>
                    <Tooltip labelFormatter={(value, _) => secToText(value)}/>
                    <Legend />
                    <ReferenceLine x={maxTime} stroke="pink"/>
                    <ReferenceLine y={maxLevel} stroke="pink" />
                    <Line
                      type="monotone"
                      dataKey="level"
                      stroke="#82ca9d"
                      activeDot={{
                        onClick: (_: DotProps, payload: RechartsDotPayload) => {
                          setStartAt(payload.payload.time);
                          setAutoplay(true);
                        },
                        cursor: 'pointer',
                        stroke: 'green',
                        strokeWidth: 10,
                      }}
                      unit="%"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                  <Text type="secondary">グラフのをクリックすると頂点の時刻から動画を再生できます</Text>
                </Space>
              </div>
          }
        </Col>
      </Row>
      <Row justify="center">
        <Col>
          <Comment
            actions={[
              <span>
                <Text
                  keyboard
                  onClick={()=>{
                    setStartAt(maxTime);
                    setAutoplay(true);
                  }}
                >
                  <CaretRightOutlined /> 再生する
                </Text>
              </span>, 

              <span>
                
                  <TwitterShareButton
                    url={`\nhttps://v.echoreq.com/hololive/ より\nhttps://www.youtube.com/watch?v=${props.videoId}&t=${maxTime}s\n`}
                    title={`『${props.title}』の1番の見どころは"${secToText(maxTime)}"のあたり↓\n`}
                    related={["echoreq"]}
                    hashtags={props.channel.hashTags}
                  >
                    <Text keyboard>
                      <TwitterOutlined /> tweetする
                    </Text>
                  </TwitterShareButton>
                
              </span>
            ]}
            avatar={<Avatar src={props.channel.channelIcon} alt="channel icon" />}
            content={
              <p>
                『{props.title}』の1番の見どころは<Text mark>{secToText(maxTime)}</Text>のあたりでした。
              </p>
            }
          />
        </Col>
      </Row>
    </div>
  );
}

export default GraphArea
